import {forwardRef, useCallback, useEffect, useState, useMemo, useRef, useLayoutEffect} from 'react';
import * as Router from 'react-router-dom';
import {getLocale, setLocale, useT} from './i18n';
import {localizedPath, pageLocale, unlocalizedPath} from './locale-path';
import {loadPageMessages, tx} from './page-text';

export {Outlet, Route, useLocation, useParams} from 'react-router-dom';

const external = (to: Router.To) => typeof to === 'string' && /^(?:[a-z][a-z\d+.-]*:|\/\/)/i.test(to);
function target(to: Router.To, from: string): Router.To {
  if (external(to)) return to;
  const resolved = Router.resolvePath(to, from);
  return {...resolved, pathname: localizedPath(resolved.pathname, pageLocale(resolved.pathname) ?? getLocale())};
}

/** Keep the same route tree mounted while language changes, including a live publisher and unsaved forms. */
export function Routes({children}: {children: React.ReactNode}) {
  const location = Router.useLocation(), navigate = Router.useNavigate();
  const {locale} = useT();
  const code = pageLocale(location.pathname);
  const [languageError, setLanguageError] = useState(false), [retry, setRetry] = useState(0);
  useEffect(() => {
    let current = true;
    setLanguageError(false);
    if (code) void loadPageMessages(code).then(() => {if (current && code !== getLocale()) setLocale(code);}).catch(() => {if (current) setLanguageError(true);});
    return () => {current = false;};
  }, [code, retry]);
  useLayoutEffect(() => {
    if (!code) navigate({...location, pathname: localizedPath(location.pathname, locale)}, {replace: true, state: location.state});
  }, [code, locale, location.pathname, location.search, location.hash, navigate]);
  return <>{languageError && <div className="dashboard-feedback" role="alert">{tx('Could not load this language. Your current work is preserved.')} <button className="btn outline" onClick={() => setRetry(v => v + 1)}>{tx('Try again')}</button></div>}<Router.Routes location={{...location, pathname: unlocalizedPath(location.pathname)}}>{children}</Router.Routes></>;
}

export const Link = forwardRef<HTMLAnchorElement, Router.LinkProps>(function Link({to, relative, ...props}, ref) {
  useT();
  const resolved = Router.useResolvedPath(to, {relative});
  return <Router.Link {...props} ref={ref} to={external(to) ? to : target(resolved, '/')} />;
});

export const NavLink = forwardRef<HTMLAnchorElement, Router.NavLinkProps>(function NavLink({to, relative, end = false, caseSensitive = false, className, style, children, ...props}, ref) {
  const location = Router.useLocation();
  const resolved = Router.useResolvedPath(to, {relative});
  const path = unlocalizedPath(resolved.pathname);
  const isActive = !!Router.matchPath({path: path === '/' ? '/' : path.replace(/\/$/, '') + (end ? '' : '/*'), end: true, caseSensitive}, location.pathname);
  const state = {isActive, isPending: false, isTransitioning: false};
  return <Link {...props} ref={ref} to={to} relative={relative} aria-current={isActive ? 'page' : undefined} className={typeof className === 'function' ? className(state) : [className, isActive ? 'active' : ''].filter(Boolean).join(' ')} style={typeof style === 'function' ? style(state) : style}>{typeof children === 'function' ? children(state) : children}</Link>;
});

export function useNavigate(): Router.NavigateFunction {
  const navigate = Router.useNavigate(), location = Router.useLocation(), route = Router.useResolvedPath('.');
  return useCallback(((to: Router.To | number, options?: Router.NavigateOptions) => {
    if (typeof to === 'number') return navigate(to);
    return navigate(target(to, options?.relative === 'path' ? location.pathname : route.pathname), options);
  }) as Router.NavigateFunction, [navigate, location.pathname, route.pathname]);
}

export function Navigate({to, replace, state, relative}: Router.NavigateProps) {
  const navigate = useNavigate();
  const serialized = JSON.stringify(to);
  useEffect(() => {navigate(JSON.parse(serialized), {replace, state, relative});}, [navigate, serialized, replace, state, relative]);
  return null;
}

export function useSearchParams(defaultInit?: Router.URLSearchParamsInit): [URLSearchParams, Router.SetURLSearchParams] {
  const navigate = useNavigate(), location = Router.useLocation();
  const defaults = useRef(Router.createSearchParams(defaultInit)), hasSet = useRef(false);
  const params = useMemo(() => {
    const next = Router.createSearchParams(location.search);
    if (!hasSet.current) defaults.current.forEach((_, key) => {if (!next.has(key)) defaults.current.getAll(key).forEach(value => next.append(key, value));});
    return next;
  }, [location.search]);
  const set = useCallback<Router.SetURLSearchParams>((next, options) => {
    const search = Router.createSearchParams(typeof next === 'function' ? next(params) : next);
    hasSet.current = true;
    navigate({pathname: location.pathname, search: '?' + search.toString()}, options);
  }, [params, navigate, location.pathname]);
  return [params, set];
}
